import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    FormField,
    Input,
    KeyValuePairs,
    Modal,
    SpaceBetween,
    StatusIndicator,
} from '@cloudscape-design/components';

export type TriggerRunMode = 'LIVE' | 'MOCK_SCANNER_REAL_BUY' | 'MOCK_BOTH';

export interface TriggerJobConfirmationModalProps {
    credentialLabel: string;
    runMode: TriggerRunMode;
    mockArticleId?: string;
    mockInjectAtAttempt?: string;
    onRun: () => Promise<void>;
    onDismiss: () => void;
    loading?: boolean;
}

const RUN_LITERAL = 'RUN';

const RUN_MODE_LABEL: Record<TriggerRunMode, string> = {
    LIVE: 'Full live run',
    MOCK_SCANNER_REAL_BUY: 'Find article X, real buy',
    MOCK_BOTH: 'Mock everything',
};

const placesRealOrders = (mode: TriggerRunMode): boolean =>
    mode === 'LIVE' || mode === 'MOCK_SCANNER_REAL_BUY';

export const TriggerJobConfirmationModal: React.FC<TriggerJobConfirmationModalProps> = ({
    credentialLabel,
    runMode,
    mockArticleId,
    mockInjectAtAttempt,
    onRun,
    onDismiss,
    loading = false,
}) => {
    const [typedConfirm, setTypedConfirm] = useState<string>('');
    const realOrders = placesRealOrders(runMode);

    useEffect(() => {
        setTypedConfirm('');
    }, [runMode]);

    const typedMatchesRun = typedConfirm.trim().toUpperCase() === RUN_LITERAL;
    const primaryDisabled = loading || (realOrders && !typedMatchesRun);

    const handleRun = async () => {
        if (primaryDisabled) return;
        await onRun();
    };

    const items: { label: string; value: React.ReactNode }[] = [
        { label: 'Credential', value: credentialLabel },
        { label: 'Run mode', value: RUN_MODE_LABEL[runMode] },
        {
            label: 'Real orders',
            value: realOrders
                ? <StatusIndicator type="warning">YES — real T212 orders will fire</StatusIndicator>
                : <StatusIndicator type="success">NO — orders are mocked</StatusIndicator>,
        },
    ];
    if (runMode !== 'LIVE') {
        items.push({ label: 'Mock article ID', value: mockArticleId ?? '—' });
        items.push({ label: 'Inject at attempt', value: mockInjectAtAttempt ?? '—' });
    }

    return (
        <Modal
            visible={true}
            onDismiss={onDismiss}
            size="medium"
            header="Run scheduled trading job now?"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onDismiss} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleRun}
                            disabled={primaryDisabled}
                            loading={loading}
                        >
                            Run job
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {realOrders ? (
                    <Alert type="warning">
                        This bypasses the date guard and runs the live trading job immediately.
                        Real broker orders will be placed.
                    </Alert>
                ) : (
                    <Alert type="info">
                        This is a mocked run. No real Trading 212 orders will be placed.
                    </Alert>
                )}

                <KeyValuePairs columns={2} items={items} />

                {realOrders && (
                    <FormField
                        label={`Type ${RUN_LITERAL} to confirm`}
                        constraintText={`Required: ${RUN_LITERAL} (case-insensitive)`}
                    >
                        <Input
                            value={typedConfirm}
                            onChange={({ detail }) => setTypedConfirm(detail.value)}
                            placeholder={RUN_LITERAL}
                            disabled={loading}
                            autoFocus
                            ariaLabel="Type RUN to confirm running the live scheduled job"
                        />
                    </FormField>
                )}
            </SpaceBetween>
        </Modal>
    );
};
